// Rybbit product analytics.
//
// The loader is served first-party through our own Worker at /_insights/script.js (proxied to
// the self-hosted host at analytics.verdocs.com, see worker/index.ts) so ad-blockers (Brave,
// uBlock) can't drop it, nor the event POSTs it makes. The Rybbit script derives its collection
// base URL from its own src, so that one path is all it needs.
//
// Site ids are per-origin and the same bundle serves both domains, so the tag is injected at
// runtime instead of being hardcoded in index.html. Any origin not in SITE_IDS (localhost, stage,
// preview deploys) loads no script at all.
//
// Two hard rules. Embed routes run inside customers' apps and are never instrumented (the retired
// Segment snippet had the same gate). Anonymous signers are never identified: their events carry
// props only, and role_name is not an identifier ("Recipient 1" is a different person in every
// envelope).

const SITE_IDS: Record<string, string> = {
  'https://beta.verdocs.com': '3',
  'https://app.verdocs.com': '4',
};

const EMBED_PATH = '/embeds/';

const SIGNING_PATHS = ['/sign/', '/view/sign/', '/envelope/finish/'];

type TEventProps = Record<string, string | number | boolean>;

declare global {
  interface Window {
    rybbit?: {
      event?: (name: string, props?: TEventProps) => void;
      identify?: (userId: string) => void;
      clearUserId?: () => void;
    };
  }
}

const isEmbedOrSigningRoute = () => {
  const path = window.location.pathname;
  return path.startsWith(EMBED_PATH) || SIGNING_PATHS.some((prefix) => path.startsWith(prefix));
};

let pendingUserId: string | null = null;
let identifiedUserId: string | null = null;

const flushIdentity = () => {
  try {
    if (pendingUserId && pendingUserId !== identifiedUserId && window.rybbit?.identify) {
      window.rybbit.identify(pendingUserId);
      identifiedUserId = pendingUserId;
    }
  } catch {
    // Analytics must never throw
  }
};

/** Inject the Rybbit loader. Called once, as early as possible, from index.tsx. */
export const initAnalytics = () => {
  try {
    const siteId = SITE_IDS[window.location.origin];
    if (!siteId || window.location.pathname.startsWith(EMBED_PATH)) {
      return;
    }

    const script = document.createElement('script');
    script.src = '/_insights/script.js';
    script.defer = true;
    script.dataset.siteId = siteId;
    // The loader is deferred, so an identify() that landed before it finished is replayed here.
    script.addEventListener('load', flushIdentity);
    document.head.appendChild(script);
  } catch {
    // Analytics must never throw
  }
};

/**
 * Fire a custom event. snake_case names, scalar props only (ids, counts, flags), never user
 * content. Safe to call anywhere: a blocked, missing, or origin-disabled client all no-op.
 */
export const track = (name: string, props?: TEventProps) => {
  try {
    window.rybbit?.event?.(name, props);
  } catch {
    // Analytics must never throw
  }
};

/**
 * Signing's report of a field that didn't appear on its page (verdocs-sign's fieldNotVisible). It
 * carries the field's position and the page size, never values.
 */
export const trackFieldNotVisible = (detail: any) =>
  track('signing_field_not_visible', {
    problem: detail?.problem || '',
    envelope_id: detail?.envelope_id || '',
    org_id: detail?.organization_id || '',
    role_name: detail?.role_name || '',
    field_name: detail?.field?.name || '',
    field_type: detail?.field?.type || '',
    field_page: detail?.field?.page ?? 0,
    field_x: detail?.field?.x ?? 0,
    field_y: detail?.field?.y ?? 0,
    field_width: detail?.field?.width ?? 0,
    field_height: detail?.field?.height ?? 0,
    page_width: detail?.page?.width ?? 0,
    page_height: detail?.page?.height ?? 0,
    rendered_width: Math.round(detail?.page?.rendered_width ?? 0),
    rendered_height: Math.round(detail?.page?.rendered_height ?? 0),
  });

/**
 * Attach the signed-in user's email as the Rybbit user id. Idempotent, and self-healing once the
 * deferred loader has run. Callers must have already confirmed a real user session; signing
 * surfaces and embeds are refused here too.
 */
export const identify = (email: string) => {
  if (!email || isEmbedOrSigningRoute()) {
    return;
  }

  pendingUserId = email;
  flushIdentity();
};

/** Drop the identity on logout so the next visitor in this browser starts anonymous. */
export const clearIdentity = () => {
  pendingUserId = null;
  identifiedUserId = null;

  try {
    window.rybbit?.clearUserId?.();
  } catch {
    // Analytics must never throw
  }
};
